import React from 'react';
import {
  Modal,
  Stack,
  Text,
  Badge,
  Button,
  Group,
  Divider,
  Alert,
} from '@mantine/core';
import {
  IconPlugConnected,
  IconEdit,
  IconTrash,
  IconAlertCircle,
  IconPencil,
} from '@tabler/icons-react';
import { DeviceConfig } from '../../types/devices';

interface DeviceDetailModalProps {
  device: DeviceConfig | null;
  opened: boolean;
  onClose: () => void;
  onTest: (device: DeviceConfig) => void;
  onEdit: (device: DeviceConfig) => void;
  onDelete: (device: DeviceConfig) => void;
  onPumpAssignments?: (device: DeviceConfig) => void;
}

// Device types that support pump assignments
const PUMP_ASSIGNMENT_TYPES = ['dosetronic', 'apex_fusion', 'kamoer_stp', 'kamoer_p4', 'kamoer_kh'];

// Map device_type to display label
const getDeviceTypeLabel = (deviceType: string): string => {
  const labels: Record<string, string> = {
    apex_fusion: 'Apex Fusion (Cloud)',
    focustronic: 'Focustronic',
    aquawiz: 'AquaWiz KH1',
    aquarimate: 'Aquarimate',
    alkatronic: 'Alkatronic',
    dosetronic: 'Dosetronic',
    mastertronic: 'Mastertronic',
    kamoer_kh: 'Kamoer KH Carer',
    kamoer_stp: 'Kamoer STP Connect',
    kamoer_p4: 'Kamoer P4 Pro',
  };
  return labels[deviceType] || deviceType;
};

export const DeviceDetailModal: React.FC<DeviceDetailModalProps> = ({
  device,
  opened,
  onClose,
  onTest,
  onEdit,
  onDelete,
  onPumpAssignments,
}) => {
  if (!device) return null;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Device Details"
      fullScreen
      transitionProps={{ transition: 'slide-up' }}
    >
      <Stack gap="lg">
        {/* Device Info Section */}
        <Stack gap="xs">
          <Text size="xl" fw={600}>
            {device.display_name}
          </Text>
          <Group gap="xs">
            <Text size="sm" c="dimmed">Type:</Text>
            <Text size="sm">{getDeviceTypeLabel(device.device_type)}</Text>
          </Group>
          <Group gap="xs">
            <Text size="sm" c="dimmed">Status:</Text>
            <Badge color={device.is_enabled ? 'green' : 'gray'} size="sm">
              {device.is_enabled ? 'Enabled' : 'Disabled'}
            </Badge>
          </Group>
        </Stack>

        {/* Last Sync Section */}
        <Stack gap="xs">
          <Text size="sm" c="dimmed">Last Sync</Text>
          <Text size="sm">
            {device.last_successful_sync
              ? new Date(device.last_successful_sync).toLocaleString()
              : 'Never'}
          </Text>
        </Stack>

        {/* Error Alert (if present) */}
        {device.last_error && (
          <Alert icon={<IconAlertCircle size={16} />} color="orange" variant="light">
            <Text size="sm" fw={500}>Last Error</Text>
            <Text size="sm">{device.last_error}</Text>
          </Alert>
        )}

        <Divider />

        {/* Action Buttons */}
        <Stack gap="sm">
          {PUMP_ASSIGNMENT_TYPES.includes(device.device_type) &&
            onPumpAssignments && (
            <Button
              leftSection={<IconPencil size={18} />}
              variant="light"
              color="blue"
              fullWidth
              onClick={() => {
                onClose();
                onPumpAssignments(device);
              }}
            >
              Pump Assignments
            </Button>
          )}
          <Button
            leftSection={<IconPlugConnected size={18} />}
            variant="light"
            fullWidth
            onClick={() => onTest(device)}
          >
            Test Connection
          </Button>
          <Button
            leftSection={<IconEdit size={18} />}
            variant="light"
            fullWidth
            onClick={() => onEdit(device)}
          >
            Edit Device
          </Button>
          <Button
            leftSection={<IconTrash size={18} />}
            variant="light"
            color="red"
            fullWidth
            onClick={() => onDelete(device)}
          >
            Delete Device
          </Button>
        </Stack>
      </Stack>
    </Modal>
  );
};
